import Avatar from '@/components/avatar/Avatar';
import Card from '@/components/ux/Card';
import { formatNumber, formatTimestamp, isZeroDate, utils } from '@/helpers';
import { onSelectCampaign } from '@/pages/campaigns/CampaignsPage';
import { useMaximizeStore, useUsernamesStore } from '@/stores';
import { useNeedsAttentionStore } from '@/stores/campaigns/useNeedsAttentionStore';
import { CoMarketingEditRequestWithTemplateAndCampaign, InstalledCoMarketingTemplateWithCampaign, SponsoredCoMarketingTemplateWithCampaign } from '@/types';
import { Flex, Icon, Tag, Typography } from '@alpine-iq/ui-library';
import React from 'react';
import { getCoMarketingBudgetForUser } from '../coMarketing.utils';
import { CoMarketingStatusColumnMapping } from './CoMarketingTableHeaders';
import NeedsAttentionPage from '../pages/NeedsAttentionPage';
import { CampaignTabType } from '@/pages/campaigns/campaigns.page.utils';

export default function CoMarketingNeedsAttention() {
	const { maximize } = useMaximizeStore();
	const { loaded: needsAttentionLoaded, combinedItems, initialize, page, limit = 4 } = useNeedsAttentionStore();

	React.useEffect(() => {
		if (!needsAttentionLoaded) {
			initialize();
		}
	}, []);

	const slicedCombinedItems = React.useMemo(() => {
		return combinedItems.slice((page - 1) * limit, page * limit);
	}, [combinedItems, page, limit]);

	// Render the combined items
	const renderedItems = React.useMemo(() => {
		return slicedCombinedItems.map((item) => {
			if (!('type' in item)) {
				return (
					<EditRequestItem
						key={item.id}
						editRequest={item}
					/>
				);
			}

			if (item.type === 'sponsored') {
				return (
					<SponsoredTemplateItem
						key={item.id}
						template={item as unknown as SponsoredCoMarketingTemplateWithCampaign}
					/>
				);
			}

			return (
				<InstalledTemplateItem
					key={item.id}
					template={item as unknown as InstalledCoMarketingTemplateWithCampaign}
				/>
			);
		});
	}, [slicedCombinedItems]);

	if (!needsAttentionLoaded || !combinedItems.length) {
		return null;
	}

	return (
		<>
			<Flex
				column
				gap="16"
			>
				<Flex
					align="center"
					justify="space-between"
				>
					<Typography variant="h2">Needs Attention</Typography>
					{combinedItems.length > 4 && (
						<Flex
							gap="4"
							align="center"
							style={{ cursor: 'pointer' }}
							onClick={() => {
								maximize(() => <NeedsAttentionPage />, { maximizedKey: 'needs-attention-co-marketing' });
							}}
						>
							<Typography
								variant="body"
								style={{ color: 'var(--brand-primary)', fontWeight: 700 }}
							>
								View All
							</Typography>
							<Icon
								height="16"
								width="16"
								type="CHEVRON_RIGHT"
								color="var(--brand-primary)"
							/>
						</Flex>
					)}
				</Flex>

				<Flex gap="16">{renderedItems}</Flex>
			</Flex>
		</>
	);
}

export function CoMarketingNeedsAttentionNotificationIcon({ tab }: { tab: CampaignTabType }) {
	const { loaded: needsAttentionLoaded, combinedItems, totalSponsoredTemplates, initialize } = useNeedsAttentionStore();

	React.useEffect(() => {
		if (!needsAttentionLoaded) {
			initialize();
		}
	}, []);

	const shownLength = React.useMemo(() => {
		if (tab === 'sponsored') {
			return totalSponsoredTemplates;
		}
		return combinedItems.length;
	}, [tab, combinedItems, totalSponsoredTemplates]);

	if (shownLength <= 0) {
		return null;
	}

	return (
		<Flex
			css={`
				border-radius: 20px;
				background: var(--surface-brand, #6262f5);
				display: flex;
				padding: 1px 8px 2px 8px;
				align-items: center;
				gap: 8px;
				color: var(--text-primary-invert, #fff) !important;
				text-align: center;
				font-family: Manrope;
				font-size: 12px;
				font-style: normal;
				font-weight: 700;
				line-height: 18px; /* 150% */
				letter-spacing: 1px;
				text-transform: uppercase;
			`}
		>
			{formatNumber(shownLength)}
		</Flex>
	);
}

function EditRequestItem({ editRequest }: { editRequest: CoMarketingEditRequestWithTemplateAndCampaign }) {
	const { usernames, getUsername } = useUsernamesStore();

	const installedTemplate = editRequest.template;

	const userID = editRequest.triggerUID;

	const username = React.useMemo(() => {
		if (!userID) return '';
		return usernames[userID] ?? getUsername(userID, true);
	}, [userID, usernames, getUsername]);

	const approvalStatus = editRequest.approvalStatus;

	const colorData = CoMarketingStatusColumnMapping?.[approvalStatus];

	const borderColor = colorData?.style?.borderColor ?? 'var(--gray200)';
	const backgroundColor = colorData?.style?.backgroundColor ?? 'var(--gray100)';
	const textColor = colorData?.style?.color ?? 'var(--text-primary)';

	const campaign = editRequest?.template?.campaign;

	return (
		<Card
			borderWidth="1px"
			style={{ borderRadius: 16, flex: 1, cursor: 'pointer', maxWidth: 350 }}
			onClick={() => {
				const isInstaller = installedTemplate?.userID === utils.uid;
				if (campaign?.id) {
					onSelectCampaign(campaign?.id, isInstaller ? undefined : installedTemplate?.id?.toString());
				}
			}}
		>
			<Flex
				column
				gap="16"
			>
				<Flex
					align="center"
					justify="space-between"
				>
					<Flex
						align="center"
						gap="8"
					>
						<Avatar
							uid={userID}
							size={24}
						/>
						<Typography variant="h3">{username}</Typography>
					</Flex>
					<Tag
						style={{
							borderColor: borderColor,
							backgroundColor: backgroundColor,
							color: textColor,
						}}
					>
						<span style={{ color: textColor }}>{approvalStatus === 'pending' ? 'New Edits' : 'Edits Denied'}</span>
					</Tag>
				</Flex>
				<Flex
					column
					gap="4"
				>
					<Typography variant="label">Campaign Name</Typography>
					<Typography
						variant="body"
						style={{ fontWeight: 700 }}
					>
						{campaign?.name}
					</Typography>
				</Flex>
				<Flex align="center">
					{installedTemplate?.isPaid && (
						<Flex
							column
							gap="4"
						>
							<Typography variant="label">Budget</Typography>
							<Typography variant="body">{getCoMarketingBudgetForUser(installedTemplate, installedTemplate?.userID)}</Typography>
						</Flex>
					)}
				</Flex>
			</Flex>
		</Card>
	);
}

function SponsoredTemplateItem({ template }: { template: SponsoredCoMarketingTemplateWithCampaign }) {
	const { usernames, getUsername } = useUsernamesStore();

	const username = React.useMemo(() => {
		if (!template.userID) return '';
		return usernames[template.userID] ?? getUsername(template.userID, true);
	}, [template.userID, usernames, getUsername]);

	return (
		<Card
			borderWidth="1px"
			style={{ borderRadius: 16, flex: 1, cursor: 'pointer', maxWidth: 350 }}
			onClick={() => {
				onSelectCampaign(template.campaign?.id, template.id?.toString());
			}}
		>
			<Flex
				column
				gap="16"
			>
				<Flex
					align="center"
					justify="space-between"
				>
					<Flex
						align="center"
						gap="8"
					>
						<Avatar
							uid={template.userID}
							size={24}
						/>
						<Typography variant="h3">{username}</Typography>
					</Flex>
					<Tag
						style={{
							borderColor: 'var(--green100)',
							backgroundColor: 'var(--green100)',
							color: 'var(--green500)',
						}}
					>
						Proposal
					</Tag>
				</Flex>
				<Flex
					column
					gap="4"
				>
					<Typography variant="label">Campaign Name</Typography>
					<Typography
						variant="body"
						style={{ fontWeight: 700 }}
					>
						{template.campaign?.name}
					</Typography>
				</Flex>
				<Flex align="center">
					{!isZeroDate(template.startDate) && (
						<Flex
							column
							gap="4"
							flex
						>
							<Typography variant="label">Start Date</Typography>
							<Typography variant="body">{formatTimestamp(template.startDate, { format: 'MM/DD/YYYY' })}</Typography>
						</Flex>
					)}
					{!isZeroDate(template.endDate) && (
						<Flex
							column
							gap="4"
							flex
						>
							<Typography variant="label">End Date</Typography>
							<Typography variant="body">{formatTimestamp(template.endDate, { format: 'MM/DD/YYYY' })}</Typography>
						</Flex>
					)}
					{template.isPaid && (
						<Flex
							column
							gap="4"
							flex
						>
							<Typography variant="label">Budget</Typography>
							<Typography variant="body">{getCoMarketingBudgetForUser(template, utils.uid)}</Typography>
						</Flex>
					)}
				</Flex>
			</Flex>
		</Card>
	);
}
function InstalledTemplateItem({ template }: { template: InstalledCoMarketingTemplateWithCampaign }) {
	const { usernames, getUsername } = useUsernamesStore();

	const userID = template.installedFromUserID ?? '';

	const username = React.useMemo(() => {
		if (!userID) return '';
		return usernames[userID] ?? getUsername(userID, true);
	}, [userID, usernames, getUsername]);

	return (
		<Card
			borderWidth="1px"
			style={{ borderRadius: 16, flex: 1, cursor: 'pointer', maxWidth: 350 }}
			onClick={() => {
				onSelectCampaign(template.campaignID);
			}}
		>
			<Flex
				column
				gap="16"
			>
				<Flex
					align="center"
					justify="space-between"
				>
					<Flex
						align="center"
						gap="8"
					>
						<Avatar
							uid={userID}
							size={24}
						/>
						<Typography variant="h3">{username}</Typography>
					</Flex>
					<Tag
						style={{
							borderColor: 'var(--green100)',
							backgroundColor: 'var(--green100)',
							color: 'var(--green500)',
						}}
					>
						Ready to Launch
					</Tag>
				</Flex>
				<Flex
					column
					gap="4"
				>
					<Typography variant="label">Campaign Name</Typography>
					<Typography
						variant="body"
						style={{ fontWeight: 700 }}
					>
						{template.campaign?.name}
					</Typography>
				</Flex>
				<Flex align="center">
					{template.isPaid && (
						<Flex
							column
							gap="4"
							flex
						>
							<Typography variant="label">Budget</Typography>
							<Typography variant="body">{getCoMarketingBudgetForUser(template, utils.uid)}</Typography>
						</Flex>
					)}
					{!isZeroDate(template.created) && (
						<Flex
							column
							gap="4"
							flex
						>
							<Typography variant="label">Installed On</Typography>
							<Typography variant="body">{formatTimestamp(template.created, { format: 'MM/DD/YYYY' })}</Typography>
						</Flex>
					)}
				</Flex>
			</Flex>
		</Card>
	);
}
